// Product analytics for the Explore map's Community filters.
//
// One `community_filters_applied` event per applied filter set, not per mutation: a
// slider commits its min and max as two mutations, "Clear" resets every filter in one
// go, and quick checkbox clicks each re-run the search. The tracker coalesces those
// bursts with a trailing delay and drops a set identical to the last one sent, so the
// event reads as "the user settled on these filters". Low volume — a handful per
// Explore session at most.

export const COMMUNITY_FILTERS_APPLIED_EVENT = 'community_filters_applied';

export type CommunityFilterKey =
  | 'distance' | 'elevation_gain' | 'duration' | 'route_type'
  | 'feature_tags' | 'activity_tags' | 'item_type';

/** The store mutations that change a Community filter (namespaced). */
export const COMMUNITY_FILTER_MUTATIONS: ReadonlySet<string> = new Set([
  'exploreV2/setDistanceRangeMin',
  'exploreV2/setDistanceRangeMax',
  'exploreV2/setCommunityDurationRangeMin',
  'exploreV2/setCommunityDurationRangeMax',
  'exploreV2/setCommunityElevationRangeMin',
  'exploreV2/setCommunityElevationRangeMax',
  'exploreV2/removeCommunityItemType',
  'exploreV2/addCommunityItemType',
  'exploreV2/setSelectedCommunityItemTypes',
  'exploreV2/toggleCommunityTrailGeometryType',
  'exploreV2/setSelectedCommunityTrailGeometryTypes',
  'exploreV2/toggleCommunityFeatureTag',
  'exploreV2/setSelectedCommunityFeatureTags',
  'exploreV2/toggleCommunityActivityTag',
  'exploreV2/setSelectedCommunityActivityTags',
]);

/** The slider bounds the "is it active" checks compare against (ExploreV2State statics). */
export interface CommunityFilterBounds {
  distanceRangeMin: number;
  distanceRangeMax: number;
  communityDurationRangeMin: number;
  communityDurationRangeMax: number;
  communityElevationRangeMin: number;
  communityElevationRangeMax: number;
}

/** The slice of the exploreV2 state the event reads. */
export interface CommunityFilterState {
  selectedDistanceRangeMin?: number | null;
  selectedDistanceRangeMax?: number | null;
  selectedCommunityDurationRangeMin?: number | null;
  selectedCommunityDurationRangeMax?: number | null;
  selectedCommunityElevationRangeMin?: number | null;
  selectedCommunityElevationRangeMax?: number | null;
  selectedCommunityItemTypes?: string[] | null;
  selectedCommunityTrailGeometryTypes?: string[] | null;
  selectedCommunityFeatureTags?: string[] | null;
  selectedCommunityActivityTags?: string[] | null;
}

export interface CommunityFiltersAppliedProps {
  filters: CommunityFilterKey[];
  route_types: string[];
  feature_tag_count: number;
  activity_tag_count: number;
  /** `all` unless exactly one of Hikes / Recordings is picked. */
  item_type: 'all' | 'hike' | 'recording';
  platform: 'web';
}

const outside = (min: number | null | undefined, max: number | null | undefined, lo: number, hi: number) =>
  (min ?? lo) > lo || (max ?? hi) < hi;

const list = (values: string[] | null | undefined): string[] =>
  Array.isArray(values) ? values.filter((v) => typeof v === 'string' && v.length > 0) : [];

export function communityFiltersAppliedProps(
  state: CommunityFilterState,
  bounds: CommunityFilterBounds,
): CommunityFiltersAppliedProps {
  const itemTypes = Array.from(new Set(list(state.selectedCommunityItemTypes).map((t) => t.toLowerCase())));
  const item_type: CommunityFiltersAppliedProps['item_type'] =
    itemTypes.length === 1 && (itemTypes[0] === 'hike' || itemTypes[0] === 'recording') ? itemTypes[0] : 'all';
  const route_types = [...list(state.selectedCommunityTrailGeometryTypes)].sort();
  const featureTags = list(state.selectedCommunityFeatureTags);
  const activityTags = list(state.selectedCommunityActivityTags);

  const filters: CommunityFilterKey[] = [];
  if (outside(state.selectedDistanceRangeMin, state.selectedDistanceRangeMax, bounds.distanceRangeMin, bounds.distanceRangeMax)) {
    filters.push('distance');
  }
  if (outside(state.selectedCommunityElevationRangeMin, state.selectedCommunityElevationRangeMax,
    bounds.communityElevationRangeMin, bounds.communityElevationRangeMax)) {
    filters.push('elevation_gain');
  }
  if (outside(state.selectedCommunityDurationRangeMin, state.selectedCommunityDurationRangeMax,
    bounds.communityDurationRangeMin, bounds.communityDurationRangeMax)) {
    filters.push('duration');
  }
  if (route_types.length > 0) filters.push('route_type');
  if (featureTags.length > 0) filters.push('feature_tags');
  if (activityTags.length > 0) filters.push('activity_tags');
  if (item_type !== 'all') filters.push('item_type');

  return {
    filters,
    route_types,
    feature_tag_count: featureTags.length,
    activity_tag_count: activityTags.length,
    item_type,
    platform: 'web',
  };
}

export interface CommunityFiltersTrackerOptions {
  bounds: CommunityFilterBounds;
  track: (event: string, props: CommunityFiltersAppliedProps) => void;
  /** Trailing delay that folds a burst of mutations into one apply. */
  delayMs?: number;
}

/**
 * Call `notify(state)` after every Community filter mutation; the event goes out once
 * the mutations stop for `delayMs`, and only when the filter set differs from the last
 * one sent (the untouched set counts as sent, so opening Community sends nothing).
 */
export function createCommunityFiltersTracker({ bounds, track, delayMs = 1500 }: CommunityFiltersTrackerOptions) {
  const signature = (props: CommunityFiltersAppliedProps) => JSON.stringify(props);
  let lastSent = signature(communityFiltersAppliedProps({}, bounds));
  let timer: ReturnType<typeof setTimeout> | null = null;

  return {
    notify(getState: () => CommunityFilterState & { searchSource?: string }) {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        const state = getState();
        if (state.searchSource !== 'community') return;
        const props = communityFiltersAppliedProps(state, bounds);
        const sig = signature(props);
        if (sig === lastSent) return;
        lastSent = sig;
        try {
          track(COMMUNITY_FILTERS_APPLIED_EVENT, props);
        } catch {
          // analytics must never break a search
        }
      }, delayMs);
    },
  };
}
